/**
 * Why a sandbox start is taking a while, and roughly how long is left.
 *
 * The api attaches a `wait` to a boot progress event when a start is held: in
 * line behind other starts, waiting for room on the server, restoring the
 * project from its backup or archive, or building a setup with nothing cached
 * (`molecule-dev/api/src/sandbox/boot-wait.ts`). This shows one plain sentence
 * for the reason and a live time line:
 *
 * - a measured estimate → "About 1:20 left", counting down;
 * - the estimate ran out → "Taking longer than usual" plus the time so far
 *   (for a place in line, only once the wait is 3 minutes past its usual
 *   length — until then the time so far only);
 * - no estimate yet → the time so far only. Never an invented number.
 *
 * @module
 */

import { useEffect, useState } from 'react'

import { useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'

/**
 * Why a start waits. Mirrors the api's `BootWaitReason`
 * (`molecule-dev/api/src/sandbox/progress-steps.ts`) across the api↔app
 * boundary, which can't share types.
 */
export type BootWaitReason =
  'queue' | 'server-room' | 'busy-clearing-space' | 'restoring-backup' | 'restoring' | 'cold-boot'

/** A wait as it arrives on the `progress` event. Mirrors the api's `BootWait`. */
export interface BootWait {
  phase: string
  reason: BootWaitReason
  /** Seconds left when the event was sent; null when unknown or overrun. */
  etaSeconds: number | null
  /** The typical total length of this kind of wait, seconds; null when unknown. */
  typicalSeconds: number | null
  /** When the wait began (epoch ms, server clock). */
  since: number
  /** How long the wait had lasted when the event was sent, seconds; absent from an older server. */
  elapsedSeconds?: number
  /** 1-based place in line. */
  position?: number
}

const REASONS: ReadonlySet<string> = new Set([
  'queue',
  'server-room',
  'busy-clearing-space',
  'restoring-backup',
  'restoring',
  'cold-boot',
])

/**
 * Read a `wait` off a progress event, or null when it is missing or malformed.
 *
 * @param value - `data.wait` from the SSE event.
 * @returns The wait, or null.
 */
export function parseBootWait(value: unknown): BootWait | null {
  if (!value || typeof value !== 'object') return null
  const w = value as Record<string, unknown>
  if (typeof w.reason !== 'string' || !REASONS.has(w.reason)) return null
  if (typeof w.since !== 'number' || !Number.isFinite(w.since)) return null
  const num = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null
  return {
    phase: typeof w.phase === 'string' ? w.phase : '',
    reason: w.reason as BootWaitReason,
    etaSeconds: num(w.etaSeconds),
    typicalSeconds: num(w.typicalSeconds),
    since: w.since,
    ...(typeof w.elapsedSeconds === 'number' &&
    Number.isFinite(w.elapsedSeconds) &&
    w.elapsedSeconds >= 0
      ? { elapsedSeconds: w.elapsedSeconds }
      : {}),
    ...(typeof w.position === 'number' && w.position > 0 ? { position: w.position } : {}),
  }
}

/** What the time line says right now. */
export type BootWaitTimeline =
  | { kind: 'remaining'; seconds: number }
  | { kind: 'overrun'; elapsed: number }
  | { kind: 'elapsed'; elapsed: number }

/**
 * How far past its estimate a QUEUE wait must run before the notice says
 * "Taking longer than usual": a line moves in steps (workers free in bursts),
 * so the estimate running out is not yet unusual — only 3 minutes past it is.
 */
export const QUEUE_OVERRUN_GRACE_SECONDS = 180

/**
 * The time line at `now`. Pure.
 *
 * The countdown runs from when the event ARRIVED (`receivedAt`, browser clock),
 * so a skew between the server's clock and the browser's cannot stretch or
 * shrink it. The time so far counts on from the server's own figure
 * (`elapsedSeconds`, at the same moment) the same way; an older server without
 * it falls back to `wait.since`, floored at zero.
 *
 * For a place in line (`queue`), an estimate that has run out shows the time
 * so far until the wait is {@link QUEUE_OVERRUN_GRACE_SECONDS} past its usual
 * length — only then does the notice honestly say it is taking longer than
 * usual (with the position it still holds).
 *
 * @param wait - The wait.
 * @param receivedAt - When the event carrying it arrived (epoch ms, browser clock).
 * @param now - The clock.
 * @returns The time line.
 */
export function bootWaitTimeline(
  wait: BootWait,
  receivedAt: number,
  now: number,
): BootWaitTimeline {
  const elapsed =
    wait.elapsedSeconds === undefined
      ? Math.max(0, Math.floor((now - wait.since) / 1000))
      : Math.max(0, Math.floor(wait.elapsedSeconds + (now - receivedAt) / 1000))
  const overrun = (): BootWaitTimeline => {
    if (
      wait.reason === 'queue' &&
      wait.typicalSeconds !== null &&
      elapsed < wait.typicalSeconds + QUEUE_OVERRUN_GRACE_SECONDS
    ) {
      return { kind: 'elapsed', elapsed }
    }
    return { kind: 'overrun', elapsed }
  }
  if (wait.etaSeconds !== null) {
    const seconds = Math.ceil(wait.etaSeconds - (now - receivedAt) / 1000)
    if (seconds > 0) return { kind: 'remaining', seconds }
    return overrun()
  }
  if (wait.typicalSeconds !== null) return overrun()
  return { kind: 'elapsed', elapsed }
}

/**
 * Seconds as a clock: `0:42`, `3:05`, `1:02:09`.
 *
 * @param totalSeconds - Seconds (negative reads as zero).
 * @returns The clock text.
 */
export function formatWaitClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

/**
 * Whether the start behind a wait can still be cancelled: only while its job
 * waits in the queue (`queued` — in line, or held while the platform frees
 * space). Once a worker runs the boot, the api refuses a Stop.
 *
 * @param wait - The wait.
 * @returns True when Cancel can work.
 */
export function isBootWaitCancellable(wait: BootWait): boolean {
  return wait.phase === 'queued'
}

/**
 * The live wait notice, shown under the boot checklist. While the start can
 * still be cancelled ({@link isBootWaitCancellable}) and `onCancel` is given,
 * it offers a Cancel button.
 *
 * @param root0 - Props.
 * @param root0.wait - The wait from the latest progress event.
 * @param root0.receivedAt - When that event arrived (epoch ms, browser clock).
 * @param root0.onCancel - Cancels the start (the stop action), or absent for no button.
 * @param root0.cancelling - Whether a cancel is in flight (the button is disabled).
 * @returns The notice.
 */
export function BootWaitNotice({
  wait,
  receivedAt,
  onCancel,
  cancelling = false,
}: {
  wait: BootWait
  receivedAt: number
  onCancel?: () => void
  cancelling?: boolean
}) {
  const { t } = useTranslation()
  const cm = getClassMap()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [wait, receivedAt])

  const reason = (() => {
    switch (wait.reason) {
      case 'queue':
        return wait.position && wait.position > 1
          ? t(
              'workspace.bootWait.queuePosition',
              { position: wait.position },
              {
                defaultValue:
                  'Waiting for an available sandbox. You are number {{position}} in line.',
              },
            )
          : t('workspace.bootWait.queue', undefined, {
              defaultValue: 'Waiting for an available sandbox. You are next in line.',
            })
      case 'server-room':
        return t('workspace.bootWait.serverRoom', undefined, {
          defaultValue:
            'Waiting for room on the server while other sandboxes start or go to sleep.',
        })
      case 'busy-clearing-space':
        return t('workspace.bootWait.clearingSpace', undefined, {
          defaultValue:
            'The platform is freeing up space on the server. Your sandbox starts as soon as there is room.',
        })
      case 'restoring-backup':
        return t('workspace.bootWait.restoringBackup', undefined, {
          defaultValue: 'Restoring your project from its backup.',
        })
      case 'restoring':
        return t('workspace.bootWait.restoring', undefined, {
          defaultValue: 'Restoring your project from its archive.',
        })
      case 'cold-boot':
        return t('workspace.bootWait.coldBoot', undefined, {
          defaultValue: 'Setting this project up from scratch. The next start will be faster.',
        })
    }
  })()

  const timeline = bootWaitTimeline(wait, receivedAt, now)
  const time =
    timeline.kind === 'remaining'
      ? t(
          'workspace.bootWait.remaining',
          { time: formatWaitClock(timeline.seconds) },
          { defaultValue: 'About {{time}} left' },
        )
      : timeline.kind === 'overrun'
        ? t(
            'workspace.bootWait.overrun',
            { time: formatWaitClock(timeline.elapsed) },
            { defaultValue: 'Taking longer than usual · {{time}} so far' },
          )
        : t(
            'workspace.bootWait.elapsed',
            { time: formatWaitClock(timeline.elapsed) },
            { defaultValue: '{{time}} so far' },
          )

  return (
    <div
      data-mol-id="boot-wait-notice"
      className={cm.cn(cm.stack(1), cm.textCenter, cm.sp('mt', 1))}
      style={{ maxWidth: 320 }}
    >
      {/* Only the reason is announced; a clock read out every second would drown it. */}
      <span data-mol-id="boot-wait-reason" role="status" className={cm.textSize('sm')}>
        {reason}
      </span>
      <span
        data-mol-id="boot-wait-time"
        className={cm.cn(cm.textSize('xs'), cm.textMuted)}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {time}
      </span>
      {onCancel && isBootWaitCancellable(wait) && (
        // A block row in the column; the text-centred row centres the inline button.
        <div className={cm.sp('mt', 1)}>
          <button
            type="button"
            data-mol-id="boot-wait-cancel"
            className={cm.button({ variant: 'outline', size: 'sm' })}
            onClick={onCancel}
            disabled={cancelling}
          >
            {t('workspace.bootWait.cancel', undefined, { defaultValue: 'Cancel' })}
          </button>
        </div>
      )}
    </div>
  )
}
